import { Alert, AlertDescription } from "../ui/alert";
import { Calendar, CheckCircle2, Clock, Phone, XCircle, Zap } from "lucide-react";
import { FormEmbed, FormEmbedSkeleton, useSavedContactForm } from "../../forms/form-embed";
import { Input, PhoneInput } from "../ui/input";
import { formatPhone, phoneHref } from "@servicepro/utils";
import { BOLD_CONTAINER_CLASS } from "../ui/bold-container";
import type { BaseComponentProps } from '../../types/puck';
import { Button } from "../ui/button";
import { Card } from "../ui/card";
import type { Data } from '../../forms/form-data';
import { DisplayHeading } from "../ui/display-heading";
import React from "react";
import { SmartLink } from "../smart-link";
import { Textarea } from "../ui/textarea";
import { cn } from "../../lib/utils";
import { getWindowJobsiteConfig } from "../../types/runtime-config";
import { submitLead } from "../../forms/submit-lead";
import { useTurnstile } from "../../hooks/use-turnstile";

type FormTypePreset = "quick-quote" | "schedule" | "emergency" | "contact";
type UrgencyLevel = "emergency" | "this-week" | "flexible";
type ContactFormVariant = "card" | "minimal" | "highlight" | "bold";

interface FormFieldConfig {
  name: boolean;
  email: boolean;
  phone: boolean;
  message: boolean;
  service: boolean;
  address: boolean;
  dateTime: boolean;
  requiredFields: string[];
}

interface ContactHighlight {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
  href?: string;
}

const HOURS_HIGHLIGHT_LABELS = new Set(["Hours", "Emergency Hours"]),
  /**
   * The section's band when the owner set no colour, for the variants that have
   * their own. Bold closes the page in a dark band, the way the bold hero opens it.
   */
  VARIANT_BACKGROUNDS: Partial<Record<ContactFormVariant, string>> = {
    bold: "jobsite:bg-primary",
    highlight: "jobsite:bg-muted/25",
  },
  FORM_PRESETS: Record<FormTypePreset, FormFieldConfig> = {
    contact: {
      address: false,
      dateTime: false,
      email: true,
      message: true,
      name: true,
      phone: true,
      requiredFields: ["name", "email"],
      service: false,
    },
    emergency: {
      address: true,
      dateTime: false,
      email: false,
      message: false,
      name: true,
      phone: true,
      requiredFields: ["name", "phone", "address"],
      service: false,
    },
    "quick-quote": {
      address: false,
      dateTime: false,
      email: false,
      message: true,
      name: true,
      phone: true,
      requiredFields: ["name", "phone"],
      service: true,
    },
    schedule: {
      address: false,
      dateTime: true,
      email: true,
      message: false,
      name: true,
      phone: true,
      requiredFields: ["name", "phone", "email"],
      service: true,
    },
  },
  buildContactHighlights = (props: {
    businessPhone?: string;
    hoursAvailable?: string;
    formType: FormTypePreset;
  }): ContactHighlight[] => {
    const highlights: ContactHighlight[] = [];

    if (props.businessPhone) {
      highlights.push({
        icon: Phone,
        label: "Call",
        value: formatPhone(props.businessPhone),
        href: phoneHref(props.businessPhone),
      });
    }


    if (props.hoursAvailable) {
      highlights.push({
        icon: Clock,
        label: props.formType === "emergency" ? "Emergency Hours" : "Hours",
        value: props.hoursAvailable,
      });
    }

    return highlights;
  },
  generateContactPointSchema = (props: {
    businessName?: string;
    businessPhone?: string;
    businessUrl?: string;
    availableLanguage?: string;
    hoursAvailable?: string;
    formType?: FormTypePreset;
  }): Record<string, unknown> | null => {
    const {
      businessName,
      businessPhone,
      businessUrl,
      availableLanguage,
      hoursAvailable,
      formType,
    } = props;

    if (!businessPhone) {
      return null;
    }

    const contactType =
        formType === "emergency"
          ? "emergency"
          : (formType === "schedule"
            ? "reservations"
            : "customer service"),

      schema: Record<string, unknown> = {
        "@context": "https://schema.org",
        "@type": "ContactPoint",
        contactType,
      };

    if (businessPhone) {
      schema.telephone = businessPhone;
    }
    if (businessUrl) {
      schema.url = businessUrl;
    }
    if (availableLanguage) {
      schema.availableLanguage = availableLanguage;
    }
    if (hoursAvailable) {
      schema.hoursAvailable = hoursAvailable;
    }
    if ((businessName ?? "") !== "") {
      schema.areaServed = {
        "@type": "Country",
        name: "US",
      };
    }

    return schema;
  },
  noServiceOptions: string[] = [];

interface ContactFormProps extends BaseComponentProps {
  heading: string;
  subheading?: string;
  formType?: FormTypePreset;
  variant?: ContactFormVariant;
  showUrgencySelector?: "yes" | "no" | boolean;
  submitButtonText?: string;
  successMessage?: string;
  backgroundColor?: string;
  layout?: "centered" | "split";
  serviceOptions?: string[];
  schemaEnabled?: boolean;
  businessName?: string;
  businessPhone?: string;
  businessUrl?: string;
  hasTrackingPhone?: boolean;
  availableLanguage?: string;
  hoursAvailable?: string;
  /** Canonical slug for photo-upload URLs (same slug the booking widget uses). */
  businessSlug?: string;
  /**
   * The towns the business works in, as a sentence list ("Rochester, Greece,
   * and Webster"). The bold variant shows it under the phone number.
   */
  serviceAreaList?: string;
  /** The owner's offer line ("Free estimates"), the one offer we store. Bold shows it. */
  offerLine?: string | null;
  /** Dashboard preview / builder canvas: render this draft custom form (no
   *  network, no submit). `renderFields` swaps the field area for an editable
   *  Puck canvas; `panelOnly` drops the page chrome (heading/section — those
   *  belong to the jobsite page instance) and renders just the form panel.
   *  Live surfaces never set this. Mirrors BookingWidgetInline.preview. */
  preview?: {
    formData: Data;
    renderFields?: () => React.ReactNode;
    panelOnly?: boolean;
  };
}

const ContactForm: React.FC<ContactFormProps> = ({
  id,
  heading,
  subheading,
  formType = "contact",
  variant = "card",
  showUrgencySelector = "no",
  submitButtonText,
  successMessage = "Thank you! We'll be in touch soon.",
  backgroundColor,
  layout = "centered",
  serviceOptions = noServiceOptions,
  schemaEnabled = true,
  businessName,
  businessPhone,
  businessUrl,
  hasTrackingPhone,
  availableLanguage = "en",
  hoursAvailable,
  businessSlug,
  serviceAreaList,
  offerLine,
  preview,
  className,
}) => {
  const panelOnly = preview?.panelOnly === true,
    isBold = variant === "bold",
    // Bold's light type sits on its own dark band; a colour the owner set is painted instead of it.
    onDarkBand = isBold && !backgroundColor,
    schema = schemaEnabled
      ? generateContactPointSchema({
          availableLanguage,
          businessName,
          businessPhone,
          businessUrl,
          formType,
          hoursAvailable,
        })
      : null,
    [formState, setFormState] = React.useState<"idle" | "submitting" | "success" | "error">(
      "idle",
    ),
    [formData, setFormData] = React.useState({
      address: "",
      email: "",
      message: "",
      name: "",
      phone: "",
      preferredDate: "",
      preferredTime: "",
      service: "",
      urgency: "" as UrgencyLevel | "",
    }),
    fieldConfig = FORM_PRESETS[formType],

    // A saved custom form REPLACES the preset field list — but only for the
    // "contact" preset (quick-quote/schedule/emergency stay preset-driven), and
    // Only when the business has one (null → preset markup, unchanged). While
    // The read is in flight a skeleton holds the spot, so a saved form never
    // Flashes the preset first. See wiki/decisions/form-builder-contact-tasks.md.
    saved = useSavedContactForm(!preview && formType === "contact"),
    customForm = preview?.formData ?? (formType === "contact" ? saved.data : null),
    resolvingForm = !preview && formType === "contact" && saved.loading,
    contactHighlights = buildContactHighlights({
      businessPhone,
      formType,
      hoursAvailable,
    }),
    shouldShowUrgency = () => {
      if (typeof showUrgencySelector === "boolean") {
        return showUrgencySelector;
      }
      return showUrgencySelector === "yes";
    },
    getDefaultSubmitText = () => {
      if (submitButtonText) {
        return submitButtonText;
      }

      switch (formType) {
        case "quick-quote": {
          return "Get Free Quote";
        }
        case "schedule": {
          return "Schedule Service";
        }
        case "emergency": {
          return "Request Emergency Service";
        }
        default: {
          return isBold ? "Send" : "Send Message";
        }
      }
    },
    /**
     * A preset field as the bold form shows it: its name above the box, where it
     * stays while someone types. The other styles keep the name in the placeholder.
     * @param {string} label The field's name.
     * @param {React.ReactElement} control The input.
     * @returns {React.ReactElement} The input, labelled on bold.
     */
    labelled = (label: string, control: React.ReactElement): React.ReactElement => {
      if (!isBold) {
        return control;
      }
      return (
        <label className="jobsite:grid jobsite:gap-1.5">
          <span className="jobsite:text-sm jobsite:font-semibold jobsite:text-card-foreground">{label}</span>
          {control}
        </label>
      );
    },
    /**
     * The placeholder: the field's name on the styles that show no label, nothing on bold.
     * @param {string} text The placeholder the other styles show.
     * @returns {string | undefined} The placeholder, or undefined on bold.
     */
    placeholderFor = (text: string): string | undefined => (isBold ? undefined : text),
    isRequired = (fieldName: string) => fieldConfig.requiredFields.includes(fieldName),
    handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setFormData((prev) => ({
        ...prev,
        [e.target.name]: e.target.value,
      }));
    },
    handleUrgencySelect = (level: UrgencyLevel) => {
      setFormData((prev) => ({ ...prev, urgency: level }));
    },

    {
      tokenRef: turnstileTokenRef,
      containerRef: turnstileContainerRef,
      containerClassName: turnstileClassName,
    } = useTurnstile(),

    handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      const formEl = e.currentTarget;
      if (!formEl.checkValidity()) {
        formEl.reportValidity();
        return;
      }

      const config = getWindowJobsiteConfig(),
        turnstileToken = turnstileTokenRef.current;
      if ((config?.turnstileSiteKey ?? "") !== "" && !turnstileToken) {
        setFormState("error");
        return;
      }

      setFormState("submitting");

      try {
        if (config?.apiUrl && config?.siteId) {
          const params = new URLSearchParams(window.location.search),
            isPreview = params.get("preview") === "true",
            sessionId = isPreview ? null : sessionStorage.getItem("sp_session_id") || null,

            result = await submitLead({
              apiUrl: config.apiUrl,
              backupApiUrl: config.backupApiUrl,
              businessName: config.business?.name,
              payload: {
                ...formData,
                formType,
                sessionId,
                variant: config?.variant,
                ...(turnstileToken ? { turnstileToken } : {}),
              },
              siteId: config.siteId,
            });
          if (result.status === "failed") {
            throw new Error("Lead submission failed");
          }
        }

        setFormState("success");
        setFormData({
          address: "",
          email: "",
          message: "",
          name: "",
          phone: "",
          preferredDate: "",
          preferredTime: "",
          service: "",
          urgency: "",
        });
        window.setTimeout(() => {
          setFormState("idle");
        }, 5000);
      } catch {
        setFormState("error");
        window.setTimeout(() => {
          setFormState("idle");
        }, 3000);
      }
    },
    formPanelClass = cn(
      "jobsite:p-6 jobsite:sm:p-8",
      variant === "card" &&
        "jobsite:border jobsite:border-border/80 jobsite:bg-background jobsite:shadow-xl",
      variant === "minimal" && "jobsite:border jobsite:border-border/70 jobsite:bg-background",
      variant === "highlight" &&
        "jobsite:border jobsite:border-primary/10 jobsite:bg-background jobsite:shadow-2xl",
      // White for the fields, an accent bar on top like the bold hero's cards.
      isBold && "jobsite:border-0 jobsite:border-t-[6px] jobsite:border-accent jobsite:bg-card jobsite:shadow-2xl",
    ),
    selectFieldClass =
      "jobsite:h-12 jobsite:w-full jobsite:border jobsite:border-input jobsite:bg-transparent jobsite:px-3 jobsite:text-base jobsite:text-foreground jobsite:transition-[color,box-shadow] jobsite:outline-none jobsite:focus-visible:border-ring jobsite:focus-visible:ring-ring/50 jobsite:focus-visible:ring-[3px] jobsite:disabled:pointer-events-none jobsite:disabled:cursor-not-allowed jobsite:disabled:opacity-50",
    sectionClass = cn(
      "jobsite:py-16 jobsite:md:py-24",
      backgroundColor ? "" : (VARIANT_BACKGROUNDS[variant] ?? "jobsite:bg-background"),
      className,
    ),
    urgencyButtonClass = (value: UrgencyLevel) =>
      cn(
        "jobsite:flex jobsite:flex-col jobsite:items-center jobsite:gap-1 jobsite:border jobsite:p-3 jobsite:transition-colors",
        formData.urgency === value
          ? (value === "emergency"
            ? "jobsite:border-destructive jobsite:bg-destructive/10 jobsite:text-destructive"
            : "jobsite:border-primary jobsite:bg-primary/10 jobsite:text-primary")
          : "jobsite:border-border jobsite:hover:border-primary/40",
      ),
    surfaceRadiusStyle = { borderRadius: "var(--jobsite-radius)" } as const,
    renderHighlights = () => {
      if (contactHighlights.length === 0) {
        return null;
      }

      return (
        <div className="jobsite:grid jobsite:gap-3 jobsite:sm:grid-cols-2">
          {contactHighlights.map((item) => {
            const Icon = item.icon,
              isHoursHighlight =
                HOURS_HIGHLIGHT_LABELS.has(item.label) || item.value.includes("\n"),
              content = (
                <div className="jobsite:flex jobsite:min-w-0 jobsite:items-start jobsite:gap-3">
                  <div
                    className="jobsite:flex jobsite:h-10 jobsite:w-10 jobsite:shrink-0 jobsite:items-center jobsite:justify-center jobsite:bg-primary/10 jobsite:text-primary"
                    style={surfaceRadiusStyle}
                  >
                    <Icon className="jobsite:h-4 jobsite:w-4" />
                  </div>
                  <div className="jobsite:min-w-0">
                    <p className="jobsite:text-xs jobsite:font-semibold jobsite:uppercase jobsite:tracking-[0.18em] jobsite:text-primary/60">
                      {item.label}
                    </p>
                    <p
                      className={cn(
                        "jobsite:mt-1 jobsite:text-sm jobsite:font-medium jobsite:text-foreground jobsite:[overflow-wrap:anywhere]",
                        isHoursHighlight
                          ? "jobsite:whitespace-pre-line jobsite:leading-relaxed"
                          : "jobsite:leading-snug",
                      )}
                    >
                      {item.value}
                    </p>
                  </div>
                </div>
              );

            if (item.href) {
              return (
                <SmartLink
                  key={`${item.label}-${item.value}`}
                  to={item.href}
                  className={cn(
                    "jobsite:min-w-0 jobsite:border jobsite:border-border/70 jobsite:bg-background jobsite:p-4 jobsite:transition-colors jobsite:hover:border-primary/30",
                    isHoursHighlight && "jobsite:sm:col-span-2",
                  )}
                  style={surfaceRadiusStyle}
                >
                  {content}
                </SmartLink>
              );
            }
            return (
              <div
                key={`${item.label}-${item.value}`}
                className={cn(
                  "jobsite:min-w-0 jobsite:border jobsite:border-border/70 jobsite:bg-background jobsite:p-4",
                  isHoursHighlight && "jobsite:sm:col-span-2",
                )}
                style={surfaceRadiusStyle}
              >
                {content}
              </div>
            );
          })}
        </div>
      );
    },
    customFormEmbed = customForm ? (
      <FormEmbed
        data={customForm}
        renderFields={preview?.renderFields}
        disableSubmit={Boolean(preview)}
        submitLabel={getDefaultSubmitText()}
        successMessage={successMessage}
        formType={formType}
        hasTrackingPhone={hasTrackingPhone}
        businessSlug={businessSlug}
        buttonClassName={cn(
          variant === "highlight" && "jobsite:rounded-full",
          formType === "emergency" && "jobsite:bg-destructive jobsite:hover:bg-destructive/90",
        )}
      />
    ) : null,
    presetFormContent = (
      <form onSubmit={handleSubmit} className="jobsite:space-y-4">
        <div className="jobsite:grid jobsite:gap-4">
          {fieldConfig.name && labelled("Name",
            <Input
              type="text"
              name="name"
              autoComplete="name"
              placeholder={placeholderFor(`Your Name${isRequired("name") ? " *" : ""}`)}
              required={isRequired("name")}
              value={formData.name}
              onChange={handleChange}
              disabled={formState === "submitting"}
              className="jobsite:h-12 jobsite:border-2 jobsite:text-base"
            />,
          )}

          {fieldConfig.email && labelled("Email",
            <Input
              type="email"
              name="email"
              autoComplete="email"
              placeholder={placeholderFor(`Email Address${isRequired("email") ? " *" : ""}`)}
              required={isRequired("email")}
              value={formData.email}
              onChange={handleChange}
              disabled={formState === "submitting"}
              className="jobsite:h-12 jobsite:border-2 jobsite:text-base"
            />,
          )}

          {fieldConfig.phone && labelled("Phone",
            <PhoneInput
              name="phone"
              placeholder={placeholderFor(`Phone Number${isRequired("phone") ? " *" : ""}`)}
              required={isRequired("phone")}
              value={formData.phone}
              onValueChange={(phone) => {
                setFormData((prev) => ({ ...prev, phone }));
              }}
              disabled={formState === "submitting"}
              className="jobsite:h-12 jobsite:border-2 jobsite:text-base"
            />,
          )}

          {fieldConfig.service && serviceOptions.length > 0 && (
            <select
              name="service"
              value={formData.service}
              onChange={(e) => {
                setFormData((prev) => ({ ...prev, service: e.target.value }));
              }}
              disabled={formState === "submitting"}
              className={selectFieldClass}
              style={surfaceRadiusStyle}
            >
              <option value="">Select a Service</option>
              {serviceOptions.map((service) => (
                <option key={service} value={service}>
                  {service}
                </option>
              ))}
            </select>
          )}

          {fieldConfig.address && labelled("Address",
            <Input
              type="text"
              name="address"
              autoComplete="street-address"
              placeholder={placeholderFor(`Service Address${isRequired("address") ? " *" : ""}`)}
              required={isRequired("address")}
              value={formData.address}
              onChange={handleChange}
              disabled={formState === "submitting"}
              className="jobsite:h-12 jobsite:border-2 jobsite:text-base"
            />,
          )}

          {fieldConfig.dateTime && (
            <div className="jobsite:grid jobsite:gap-3 jobsite:sm:grid-cols-2">
              <Input
                type="date"
                name="preferredDate"
                value={formData.preferredDate}
                onChange={handleChange}
                disabled={formState === "submitting"}
                className="jobsite:h-12 jobsite:border-2 jobsite:text-base"
              />
              <Input
                type="time"
                name="preferredTime"
                value={formData.preferredTime}
                onChange={handleChange}
                disabled={formState === "submitting"}
                className="jobsite:h-12 jobsite:border-2 jobsite:text-base"
              />
            </div>
          )}

          {shouldShowUrgency() && (
            <div className="jobsite:space-y-2">
              <span className="jobsite:text-sm jobsite:font-medium jobsite:text-muted-foreground">
                How soon do you need service?
              </span>
              <div className="jobsite:grid jobsite:grid-cols-3 jobsite:gap-2">
                <button
                  type="button"
                  onClick={() => {
                    handleUrgencySelect("emergency");
                  }}
                  disabled={formState === "submitting"}
                  className={urgencyButtonClass("emergency")}
                  style={surfaceRadiusStyle}
                >
                  <Zap className="jobsite:h-5 jobsite:w-5" />
                  <span className="jobsite:text-xs jobsite:font-medium">Today</span>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    handleUrgencySelect("this-week");
                  }}
                  disabled={formState === "submitting"}
                  className={urgencyButtonClass("this-week")}
                  style={surfaceRadiusStyle}
                >
                  <Calendar className="jobsite:h-5 jobsite:w-5" />
                  <span className="jobsite:text-xs jobsite:font-medium">This Week</span>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    handleUrgencySelect("flexible");
                  }}
                  disabled={formState === "submitting"}
                  className={urgencyButtonClass("flexible")}
                  style={surfaceRadiusStyle}
                >
                  <Clock className="jobsite:h-5 jobsite:w-5" />
                  <span className="jobsite:text-xs jobsite:font-medium">Flexible</span>
                </button>
              </div>
            </div>
          )}

          {fieldConfig.message && labelled("Message",
            <Textarea
              name="message"
              placeholder={placeholderFor(formType === "quick-quote" ? "Describe your project..." : "Your Message")}
              required={isRequired("message")}
              rows={4}
              value={formData.message}
              onChange={handleChange}
              disabled={formState === "submitting"}
              className={cn("jobsite:resize-none jobsite:border-2 jobsite:text-base", isBold && "jobsite:border-input")}
            />,
          )}
        </div>

        {/* Turnstile widget container — CF JS API renders into this div after mount */}
        <div ref={turnstileContainerRef} className={turnstileClassName} />

        <Button
          type="submit"
          disabled={formState === "submitting"}
          className={cn(
            "jobsite:h-12 jobsite:w-full jobsite:font-semibold",
            variant === "highlight" && "jobsite:rounded-full",
            formType === "emergency" && "jobsite:bg-destructive jobsite:hover:bg-destructive/90",
          )}
          size="lg"
        >
          {formState === "submitting" ? "Sending..." : getDefaultSubmitText()}
        </Button>

        {fieldConfig.phone && hasTrackingPhone && (
          <p className="jobsite:text-[11px] jobsite:leading-relaxed jobsite:text-muted-foreground/70">
            By providing your phone number, you consent to receive calls and text messages (SMS). You
            can unsubscribe at any time by replying STOP. Message and data rates may apply.
          </p>
        )}

        {formState === "success" && (
          <Alert className="jobsite:rounded-2xl jobsite:border-emerald-200 jobsite:bg-emerald-50">
            <CheckCircle2 className="jobsite:h-5 jobsite:w-5 jobsite:text-emerald-600" />
            <AlertDescription className="jobsite:text-base jobsite:font-medium jobsite:text-emerald-700">
              {successMessage}
            </AlertDescription>
          </Alert>
        )}

        {formState === "error" && (
          <Alert className="jobsite:rounded-2xl jobsite:border-destructive/20 jobsite:bg-destructive/10">
            <XCircle className="jobsite:h-5 jobsite:w-5 jobsite:text-destructive" />
            <AlertDescription className="jobsite:text-base jobsite:font-medium jobsite:text-destructive">
              Something went wrong. Please try again.
            </AlertDescription>
          </Alert>
        )}
      </form>
    ),
    formContent = resolvingForm ? (
      <FormEmbedSkeleton />
    ) : (
      (customFormEmbed ?? presetFormContent)
    ),
    /**
     * The bold variant's contact column: the heading in capitals, the phone
     * number big enough to tap from across a room, the towns served and the
     * owner's offer line. Each line shows only with data behind it.
     * @param {boolean} centered Whether the section centres its content.
     * @returns {React.JSX.Element} The column.
     */
    renderBoldContact = (centered: boolean): React.JSX.Element => (
      // Gaps, not space-y: the heading block zeroes its own bottom margin, which space-y relies on.
      <div className={cn("jobsite:flex jobsite:flex-col jobsite:items-start jobsite:gap-6", centered && "jobsite:items-center jobsite:text-center")}>
        <DisplayHeading
          heading={heading}
          subheading={subheading}
          onDark={onDarkBand}
          centered={centered}
          className="jobsite:mb-0 jobsite:md:mb-0"
        />
        {(businessPhone ?? "") !== "" && (
          <a
            href={phoneHref(businessPhone ?? "")}
            className={cn(
              "jobsite:inline-flex jobsite:items-center jobsite:gap-3 jobsite:whitespace-nowrap jobsite:font-heading jobsite:text-[2rem] jobsite:leading-none jobsite:text-accent jobsite:underline-offset-8 jobsite:hover:underline jobsite:sm:text-5xl",
              !onDarkBand && "jobsite:text-foreground",
            )}
          >
            <Phone className="jobsite:size-7 jobsite:shrink-0 jobsite:sm:size-9" aria-hidden="true" />
            {formatPhone(businessPhone ?? "")}
          </a>
        )}
        {(serviceAreaList ?? "") !== "" && (
          <p className={cn("jobsite:max-w-xl jobsite:text-base jobsite:leading-relaxed jobsite:text-primary-foreground/80 jobsite:sm:text-lg", !onDarkBand && "jobsite:text-muted-foreground")}>
            We work in {serviceAreaList}.
          </p>
        )}
        {(offerLine?.trim() ?? "") !== "" && (
          <span
            className="jobsite:inline-flex jobsite:bg-accent jobsite:px-2.5 jobsite:py-1 jobsite:text-xs jobsite:font-bold jobsite:uppercase jobsite:tracking-[0.14em] jobsite:text-accent-foreground"
            style={surfaceRadiusStyle}
          >
            {offerLine}
          </span>
        )}
      </div>
    );

  if (panelOnly) {
    return <Card className={formPanelClass}>{formContent}</Card>;
  }

  if (layout === "split") {
    return (
      <section
        id={id || "contact"}
        className={sectionClass}
        style={{ backgroundColor: backgroundColor || undefined }}
      >
        {schema && (
          <script type="application/ld+json">{JSON.stringify(schema)}</script>
        )}
        <div className={isBold ? BOLD_CONTAINER_CLASS : "jobsite:container jobsite:mx-auto jobsite:px-4"}>
          <div className="jobsite:grid jobsite:gap-10 jobsite:lg:grid-cols-[minmax(0,1fr)_minmax(320px,0.95fr)] jobsite:lg:items-start">
            {isBold ? renderBoldContact(false) : (
            <div className="jobsite:space-y-5">
              <div className="jobsite:space-y-4">
                <h2 className="jobsite:font-heading jobsite:text-4xl jobsite:text-foreground jobsite:md:text-5xl">
                  {heading}
                </h2>
                {subheading && (
                  <p className="jobsite:max-w-2xl jobsite:text-lg jobsite:text-muted-foreground jobsite:md:text-xl">
                    {subheading}
                  </p>
                )}
              </div>

              {(variant === "highlight" || contactHighlights.length > 0) && renderHighlights()}
            </div>
            )}

            <Card className={formPanelClass}>{formContent}</Card>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      id={id || "contact"}
      className={sectionClass}
      style={{ backgroundColor: backgroundColor || undefined }}
    >
      {schema && (
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
      )}
      <div className={isBold ? BOLD_CONTAINER_CLASS : "jobsite:container jobsite:mx-auto jobsite:px-4"}>
        <div className="jobsite:mx-auto jobsite:max-w-2xl">
          {isBold ? <div className="jobsite:mb-10">{renderBoldContact(true)}</div> : (
          <div className="jobsite:mb-8 jobsite:text-center">
            <h2 className="jobsite:font-heading jobsite:text-4xl jobsite:text-foreground jobsite:md:text-5xl">
              {heading}
            </h2>
            {subheading && (
              <p className="jobsite:mt-4 jobsite:text-lg jobsite:text-muted-foreground jobsite:md:text-xl">
                {subheading}
              </p>
            )}
          </div>
          )}

          {variant === "highlight" && contactHighlights.length > 0 && (
            <div className="jobsite:mb-6">{renderHighlights()}</div>
          )}

          <Card className={formPanelClass}>{formContent}</Card>
        </div>
      </div>
    </section>
  );
};

export type { FormTypePreset, UrgencyLevel, ContactFormVariant, ContactFormProps };
export { ContactForm };
